import type { DropCell, TileData } from '@words-happen/game-components';
import { anchorWithinBoard, getBoardLayout, resolveLanding } from '@words-happen/game-components';
import type { BoardGamePlacement, BoardGameTile, BoardGameView } from './board-game-types';

/**
 * The player's turn as they build it, before anything is sent: the rack in the order they keep it, the tiles laid on
 * the board, and the tiles picked to swap. The server's game view is the truth; the draft is only ever made from it.
 */
export interface BoardGameDraft {
  /** The turn the draft is for. A new turn starts a new draft. */
  turnNumber: number;
  /** Tiles in hand, in the player's order. */
  rack: TileData[];
  /** Tiles laid on the board this turn. */
  pending: TileData[];
  /** Tiles picked to swap, or null when no swap is being chosen. */
  swapTray: TileData[] | null;
}

export type BoardGameDraftAction =
  /** The server's rack, for the turn it is on. */
  | { type: 'sync'; rack: BoardGameTile[]; turnNumber: number }
  /** A rack tile landed on the board, already placed where it lands (`landRackTile`). */
  | { type: 'lay'; tile: TileData }
  /** A tile laid this turn was moved or turned on the board. */
  | { type: 'move'; tile: TileData }
  | { type: 'return'; tile: TileData }
  | { type: 'reorder'; tiles: TileData[] }
  /** Every tile laid or picked to swap goes back to the rack. */
  | { type: 'recall' }
  | { type: 'shuffle'; random?: () => number }
  | { type: 'open-swap' }
  /** The tray's tiles go back to the rack; tiles laid on the board stay. */
  | { type: 'cancel-swap' }
  /** `limit` is the most tiles the tray takes. */
  | { type: 'mark'; tile: TileData; limit: number }
  | { type: 'unmark'; tile: TileData };

/** A rack tile as the board and rack draw it: at rest, its `id` the tile ID as a string. */
export const toTileData = (tile: BoardGameTile): TileData => ({
  id: String(tile.id),
  letter: tile.face,
  points: tile.points,
  reducedPoints: tile.reducedPoints || undefined,
  row: 0,
  col: 0,
  dir: 'h',
});

const atRest = (tile: TileData): TileData => ({ ...tile, row: 0, col: 0, dir: 'h', placed: undefined });

/** A new draft for the turn: the rack as the server holds it, nothing laid. */
export const createDraft = (rack: BoardGameTile[], turnNumber: number): BoardGameDraft => ({
  turnNumber,
  rack: rack.map(toTileData),
  pending: [],
  swapTray: null,
});

const inHand = (draft: BoardGameDraft): TileData[] => [...draft.rack, ...draft.pending, ...(draft.swapTray ?? [])];

const sameIds = (a: readonly { id: string }[], b: readonly { id: string }[]): boolean => {
  const ids = new Set(a.map(({ id }) => id));
  return a.length === b.length && b.every(({ id }) => ids.has(id));
};

/**
 * The draft for the server's rack. The same turn and the same tiles keep the draft as it is, tiles on the board
 * included. Otherwise everything comes back to the rack: tiles the player still holds keep their order, and new ones
 * go on the end.
 */
export function syncDraft(draft: BoardGameDraft, rack: BoardGameTile[], turnNumber: number): BoardGameDraft {
  const server = rack.map(toTileData);
  const held = inHand(draft);
  if (draft.turnNumber === turnNumber && sameIds(held, server)) return draft;

  const order = new Map(held.map(({ id }, index) => [id, index]));
  const kept = server.filter(({ id }) => order.has(id)).sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  const drawn = server.filter(({ id }) => !order.has(id));
  return { turnNumber, rack: [...kept, ...drawn], pending: [], swapTray: null };
}

const recall = (draft: BoardGameDraft): BoardGameDraft =>
  draft.pending.length === 0 && draft.swapTray === null
    ? draft
    : {
        ...draft,
        rack: [...draft.rack, ...draft.pending.map(atRest), ...(draft.swapTray ?? [])],
        pending: [],
        swapTray: null,
      };

const shuffled = <T>(items: readonly T[], random: () => number): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const hasTile = (tiles: readonly TileData[], tile: TileData) => tiles.some(({ id }) => id === tile.id);

export function boardGameDraftReducer(draft: BoardGameDraft, action: BoardGameDraftAction): BoardGameDraft {
  switch (action.type) {
    case 'sync':
      return syncDraft(draft, action.rack, action.turnNumber);
    case 'lay':
      if (!hasTile(draft.rack, action.tile)) return draft;
      return {
        ...draft,
        rack: draft.rack.filter(({ id }) => id !== action.tile.id),
        pending: [...draft.pending, { ...action.tile, placed: false }],
      };
    case 'move':
      if (!hasTile(draft.pending, action.tile)) return draft;
      return { ...draft, pending: draft.pending.map((t) => (t.id === action.tile.id ? { ...t, ...action.tile } : t)) };
    case 'return':
      if (!hasTile(draft.pending, action.tile)) return draft;
      return {
        ...draft,
        pending: draft.pending.filter(({ id }) => id !== action.tile.id),
        rack: [...draft.rack, atRest(action.tile)],
      };
    case 'reorder':
      return sameIds(action.tiles, draft.rack) ? { ...draft, rack: action.tiles } : draft;
    case 'recall':
      return recall(draft);
    case 'shuffle':
      return { ...draft, rack: shuffled(draft.rack, action.random ?? Math.random) };
    case 'open-swap':
      return draft.swapTray === null ? { ...draft, swapTray: [] } : draft;
    case 'cancel-swap':
      return draft.swapTray === null ? draft : { ...draft, rack: [...draft.rack, ...draft.swapTray], swapTray: null };
    case 'mark': {
      const tray = draft.swapTray;
      if (tray === null || tray.length >= action.limit || !hasTile(draft.rack, action.tile)) return draft;
      const tile = draft.rack.find(({ id }) => id === action.tile.id) as TileData;
      return { ...draft, rack: draft.rack.filter(({ id }) => id !== tile.id), swapTray: [...tray, tile] };
    }
    case 'unmark': {
      const tray = draft.swapTray;
      if (tray === null || !hasTile(tray, action.tile)) return draft;
      const tile = tray.find(({ id }) => id === action.tile.id) as TileData;
      return { ...draft, swapTray: tray.filter(({ id }) => id !== tile.id), rack: [...draft.rack, tile] };
    }
  }
}

/** The tile IDs of tiles, as the moves take them. */
export const tileIds = (tiles: readonly TileData[]): number[] => tiles.map(({ id }) => Number(id));

/** The tiles laid this turn, as the play move takes them. */
export const placementsOf = (pending: readonly TileData[]): BoardGamePlacement[] =>
  pending.map(({ id, row, col, dir }) => ({ id: Number(id), row, col, dir }));

/** The board's tiles as the board draws them: committed, and played as a blank's letters where one is. */
export const boardTilesOf = (game: Pick<BoardGameView, 'board'>): TileData[] =>
  game.board.map((tile) => ({
    id: String(tile.id),
    letter: tile.letter ?? tile.face,
    points: tile.points,
    reducedPoints: tile.reducedPoints || undefined,
    blank: tile.letter ? true : undefined,
    row: tile.row,
    col: tile.col,
    dir: tile.dir,
    placed: true,
  }));

/**
 * Where a rack tile let go over `cell` lands: running across, pulled back onto the board where it would run off it,
 * and slid beside a tile already on the cell. Null when nothing near the cell can take it.
 */
export function landRackTile(
  game: Pick<BoardGameView, 'shape' | 'size' | 'diagonals'>,
  boardTiles: TileData[],
  tile: TileData,
  cell: DropCell,
): TileData | null {
  const anchor = anchorWithinBoard({ ...tile, row: cell.row, col: cell.col, dir: 'h' }, game.size);
  const landing = resolveLanding({
    tile: { ...tile, row: anchor.row, col: anchor.col, dir: 'h' },
    offset: cell.offset,
    cells: getBoardLayout(game.shape, game.size),
    boardTiles,
    size: game.size,
    diagonals: game.diagonals,
  });
  return landing ? { ...tile, row: landing.row, col: landing.col, dir: 'h', placed: false } : null;
}
